import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, State } from "@stencil/core";
import { Message } from "../message-list/interface";

@Component({
    tag: "uic-message-toast",
    styleUrl: "message-toast.scss"
})
export class MessageToast implements ComponentInterface {
    @Element() host: HTMLUicMessageToastElement;

    /**
     * How many messages should be shown as a maximum. Any new message after that will be ignored.
     */
    @Prop() readonly maxNumberOfMessages: number = 3;

    /**
     * Delete the message after x milliseconds automatically
     */
    @Prop() readonly deleteMessageAfterMillis: number = 3000;

    /**
     * How far to the right (in pixels) the message needs to be dragged to delete it
     */
    @Prop() readonly swipeDeleteThreshold: number = 100;

    /**
     * Translated 'show all messages' text
     */
    @Prop() readonly showDetailsText: string = "Show All Alarms";

    /**
     * Set whether older messages should be replaced by newer ones
     */
    @Prop() readonly replaceOlderMessages: boolean = false;

    /**
     * When 'show all messages' gets clicked, this event is triggered
     */
    @Event({ eventName: "uicShowDetailsClicked" })
    showDetailsClicked: EventEmitter<Message>;

    @State() messages: MessageP[] = [];

    private dragStartX: number;

    /**
     * @deprecated Use method addMessage instead
     */
    @Method()
    async pushMessage(message: Message, hasChildren: boolean) {
        this.addMessage(message, hasChildren);
    }

    /**
     * Adds a message to the list of messages
     * @param message The message to add
     * @param hasChildren Indicates that this one is a parent message
     */
    @Method()
    async addMessage(message: Message, hasChildren: boolean) {
        if (this.messages.length === this.maxNumberOfMessages) {
            if (this.replaceOlderMessages) {
                this.messages.splice(0, 1);
            } else {
                return;
            }
        }
        this.messages = [...this.messages, { message, hasChildren, isDeleting: false }];
        if (message.type === "Information" || message.type === "Success") {
            setTimeout(() => {
                this.removeMessage(message.messageId);
            }, this.deleteMessageAfterMillis);
        }
    }

    /**
     * @deprecated Use method updateHasChildren instead
     */
    @Method()
    async updateMessage(messageId: string, hasChildren: boolean) {
        this.updateHasChildren(messageId, hasChildren);
    }

    /**
     * Updates the hasChildren property
     *
     * @param messageId The message content and the message to update
     * @param hasChildren Indicates that this one is a parent message
     */
    @Method()
    async updateHasChildren(messageId: string, hasChildren: boolean) {
        const index = this.messages.findIndex((m) => m.message.messageId === messageId);
        if (index > -1) {
            this.messages[index].hasChildren = hasChildren;
            this.messages = [...this.messages];
        }
    }

    /**
     * @deprecated Use method removeMessage instead
     */
    @Method()
    async popMessage(message: Message) {
        this.removeMessage(message.messageId);
    }

    /**
     * Removes the first occurence of the message with the given id from the list of messages
     * @param messageId The id of the message to remove
     */
    @Method()
    async removeMessage(messageId: string) {
        const index = this.messages.findIndex((m) => m.message.messageId === messageId);
        if (index > -1) {
            this.messages[index].isDeleting = true;
            this.messages = [...this.messages];
            setTimeout(() => {
                this.messages = this.messages.filter((m) => !m.isDeleting);
            }, 100);
        }
    }

    /**
     * Clears the list of messages
     */
    @Method()
    async clearAllMessages() {
        this.messages = this.messages.map((m) => {
            m.isDeleting = true;
            return m;
        });

        setTimeout(() => {
            this.messages = this.messages.filter((m) => !m.isDeleting);
        }, 100);
    }

    private getIcon(type: string) {
        switch (type) {
            case "Success":
                return "uic:flag-success";
            case "Error":
                return "uic:flag-error";
            case "Warning":
                return "uic:flag-warning";
            case "Information":
                return "uic:flag-information";
        }
    }

    private onDragStart(screenX, event) {
        this.dragStartX = screenX;
        if (event) {
            event.dataTransfer.setDragImage(new Image(), 0, 0);
        }
    }

    private onDragEnd(screenX, event, message) {
        const draggedAmount = screenX - this.dragStartX;
        const messageElement = event.srcElement as HTMLElement;
        messageElement.style.right = `-1px`;
        if (draggedAmount > this.swipeDeleteThreshold && !(message.type === "Information") && !(message.type === "Success")) {
            this.removeMessage(message.messageId);
        }
    }

    private onDrag(screenX, event) {
        let draggedAmount = -(screenX - this.dragStartX);
        draggedAmount = draggedAmount >= 0 ? -1 : draggedAmount;
        const messageElement = event.srcElement as HTMLElement;
        messageElement.style.right = `${draggedAmount}px`;
    }

    private onDetailsClicked(event, message) {
        event.stopPropagation();
        this.showDetailsClicked.emit(message);
    }

    private openApp(event, url: string) {
        if (url) {
            window.open(url);
        }
        event.stopPropagation();
    }

    render() {
        return (
            <Host>
                <div class='uic-message-toast__container'>
                    {this.messages.map((m) => (
                        <div
                            class={{
                                "uic-message-toast__message": true,
                                "uic-message-toast__message--error": m.message.type === "Error",
                                "uic-message-toast__message--success": m.message.type === "Success",
                                "uic-message-toast__message--deleted": m.isDeleting
                            }}
                            draggable={true}
                            onDragStart={(event) => this.onDragStart(event.screenX, event)}
                            onDragEnd={(event) => this.onDragEnd(event.screenX, event, m.message)}
                            onDrag={(event) => this.onDrag(event.screenX, event)}
                            onTouchStart={(event) => this.onDragStart(event.touches[0].screenX, null)}
                            onTouchEnd={(event) => this.onDragEnd(event.changedTouches[0].screenX, event, m.message)}
                            onTouchMove={(event) => this.onDrag(event.changedTouches[0].screenX, event)}
                            onClick={(event) => this.openApp(event, m.message.sourceApplicationUrl)}
                        >
                            <div class='uic-message-toast__headline'>
                                <div class='uic-message-toast__image'>
                                    <uic-icon name={this.getIcon(m.message.type)}></uic-icon>
                                </div>
                                <div
                                    class={{
                                        "uic-message-toast__title": true,
                                        "uic-message-toast__title--error": m.message.type === "Error",
                                        "uic-message-toast__title--success": m.message.type === "Success"
                                    }}
                                >
                                    {m.message.messageTitle}
                                </div>
                                {m.message.type !== "Information" && m.message.type !== "Success" ? (
                                    <uic-icon-button
                                        class='uic-message-toast__cross'
                                        icon='uic:cross'
                                        onClick={(event) => {
                                            this.removeMessage(m.message.messageId);
                                            event.stopPropagation();
                                        }}
                                    ></uic-icon-button>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                            <div class='uic-message-toast__text'>{m.message.messageText}</div>
                            {m.hasChildren ? (
                                <div
                                    class='uic-message-toast__details-text'
                                    onClick={(event) => this.onDetailsClicked(event, m.message)}
                                >
                                    {this.showDetailsText}
                                </div>
                            ) : (
                                <div></div>
                            )}
                        </div>
                    ))}
                </div>
            </Host>
        );
    }
}

interface MessageP {
    message: Message;
    hasChildren: boolean;
    isDeleting: boolean;
}
